import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import { downloadBlob } from 'utils/downloadBlob'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'

export const RAG = { green: '#15803d', amber: '#d97706', red: '#b00020' }
export const rateColour = rate => (rate == null ? undefined : rate >= 80 ? RAG.green : rate >= 50 ? RAG.amber : RAG.red)

const PAGE = 25

const csvCell = value => {
  const text = value == null ? '' : String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const BreakdownTable = ({
  title,
  rows = [],
  columns,
  nameKey = 'name',
  nameLabel = 'Name',
  rowKey,
  defaultSort,
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing to show for this period.',
  csvName,
  onRowClick,
  isRowClickable,
  helpText,
  renderName,
  titleExtra,
}) => {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({ key: defaultSort || columns[0]?.key, dir: 'desc' })
  const [visible, setVisible] = useState(PAGE)

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = q ? rows.filter(r => String(r[nameKey] || '').toLowerCase().includes(q)) : rows
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      if (sort.key === nameKey) return String(a[nameKey] || '').localeCompare(String(b[nameKey] || '')) * dir
      const av = a[sort.key] == null ? -1 : a[sort.key]
      const bv = b[sort.key] == null ? -1 : b[sort.key]
      return (av - bv) * dir
    })
  }, [rows, search, sort, nameKey])

  const toggle = key => {
    setVisible(PAGE)
    setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === nameKey ? 'asc' : 'desc' }))
  }
  const arrow = key => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')

  const downloadCsv = () => {
    const header = [nameLabel, ...columns.map(c => c.label)]
    const lines = sorted.map(r => [r[nameKey], ...columns.map(c => (c.csv ? c.csv(r) : r[c.key]))])
    const csv = [header, ...lines].map(line => line.map(csvCell).join(',')).join('\n')
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${csvName || 'breakdown'}.csv`)
  }

  const shown = sorted.slice(0, visible)
  const cols = columns.length

  return (
    <Block>
      <Head>
        <div>
          {titleExtra ? <TitleLine><Title>{title}</Title>{titleExtra}</TitleLine> : <Title>{title}</Title>}
          {helpText && <Help>{helpText}</Help>}
        </div>
        <Controls>
          <SearchBox>
            <SearchRoundedIcon style={{ fontSize: 16 }} />
            <input value={search} onChange={e => { setSearch(e.target.value); setVisible(PAGE) }} placeholder={searchPlaceholder} aria-label={searchPlaceholder} />
          </SearchBox>
          <CsvBtn type='button' onClick={downloadCsv} disabled={!sorted.length} title={`Download this table as a spreadsheet (CSV)`}>
            <FileDownloadRoundedIcon style={{ fontSize: 16 }} />
            CSV
          </CsvBtn>
        </Controls>
      </Head>

      {sorted.length === 0 ? (
        <Empty>{search ? 'Nothing matches your search.' : emptyText}</Empty>
      ) : (
        <>
          <Scroller>
            <Table role='table' $cols={cols}>
              <Row $head role='row' $cols={cols}>
                <HeadCell role='columnheader' as='button' type='button' $left onClick={() => toggle(nameKey)}>{nameLabel}{arrow(nameKey)}</HeadCell>
                {columns.map(c => (
                  <HeadCell key={c.key} role='columnheader' as='button' type='button' title={c.title} onClick={() => toggle(c.key)}>
                    {c.label}{arrow(c.key)}
                  </HeadCell>
                ))}
              </Row>
              {shown.map(r => {
                const rowClickable = !!onRowClick && (!isRowClickable || isRowClickable(r))
                return (
                  <Row key={rowKey(r)} role='row' $cols={cols}>
                    <NameCell
                      role='cell'
                      as={rowClickable ? 'button' : 'div'}
                      type={rowClickable ? 'button' : undefined}
                      $clickable={rowClickable}
                      onClick={rowClickable ? () => onRowClick(r) : undefined}
                      title={rowClickable ? `${r[nameKey]}: open the list` : r[nameKey]}
                    >
                      {renderName ? renderName(r) : r[nameKey]}
                    </NameCell>
                    {columns.map(c => {
                      const v = r[c.key]
                      const clickable = !!(rowClickable && c.drill)
                    return (
                        <Cell
                          key={c.key}
                          role='cell'
                          as={clickable ? 'button' : 'div'}
                          type={clickable ? 'button' : undefined}
                          $c={c.colour ? c.colour(v, r) : undefined}
                          $clickable={clickable}
                          onClick={clickable ? () => onRowClick(r, c) : undefined}
                          title={c.title}
                        >
                          {c.render ? c.render(r) : v == null ? '-' : c.percent ? `${v}%` : v}
                        </Cell>
                      )
                    })}
                  </Row>
                )
              })}
            </Table>
          </Scroller>
          <InfiniteScrollSentinel hasMore={visible < sorted.length} loading={false} onLoadMore={() => setVisible(v => v + PAGE)} />
          <Foot>
            <span>Showing {shown.length} of {sorted.length}{sorted.length !== rows.length ? ` (${rows.length} in total)` : ''}</span>
          </Foot>
        </>
      )}
    </Block>
  )
}

export default BreakdownTable

const Block = styled.section`
  border: 1px solid #eceef3; border-radius: 12px; padding: 12px 14px 10px; min-width: 0; background: #fff;
`

const Head = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 8px;
`

const Title = styled.h4` margin: 0; font-size: 14px; font-weight: 800; color: #101828; `
const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`
const Help = styled.p` margin: 2px 0 0; font-size: 12.5px; color: #667085; max-width: 560px; line-height: 1.4; `

const Controls = styled.div` display: flex; align-items: center; gap: 8px; flex-wrap: wrap; `

const SearchBox = styled.label`
  display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 0 10px; border-radius: 8px;
  background: #f6f7fb; border: 1px solid #e6e8f0; color: #98a2b3;
  input { font: inherit; border: none; background: transparent; outline: none; font-size: 13px; width: 150px; color: #101828; }
  @media (max-width: 768px) { flex: 1 1 160px; input { width: 100%; } }
`

const CsvBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 0 12px; border-radius: 8px;
  border: 1px solid #d0d5dd; background: #fff; color: #344054; font-size: 13px; font-weight: 700; cursor: pointer;
  &:hover:not(:disabled) { border-color: #33becc; }
  &:disabled { opacity: 0.5; cursor: default; }
`

const Scroller = styled.div` overflow-x: auto; `

const Table = styled.div`
  display: flex; flex-direction: column; min-width: ${({ $cols }) => 180 + $cols * 78}px;
`

const Row = styled.div`
  display: grid; grid-template-columns: minmax(150px, 1.8fr) repeat(${({ $cols }) => $cols}, minmax(70px, 1fr));
  gap: 6px; align-items: center; padding: ${({ $head }) => ($head ? '4px 6px 8px' : '7px 6px')};
  border-bottom: 1px solid ${({ $head }) => ($head ? '#eef0f4' : '#f4f5f8')};
`

const HeadCell = styled.div`
  font: inherit; border: none; background: none; cursor: pointer; padding: 0; min-height: 28px;
  text-align: ${({ $left }) => ($left ? 'left' : 'right')};
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.03em; text-transform: uppercase; color: #667085;
  &:hover { color: #344054; }
`

const NameCell = styled.div`
  font: inherit; border: none; background: none; padding: 0; text-align: left; min-width: 0;
  font-size: 13px; font-weight: 700; color: #101828; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ $clickable }) => $clickable && '&:hover { color: #2aa6b3; text-decoration: underline; }'}
`

const Cell = styled.div`
  font: inherit; border: none; background: none; text-align: right; padding: 2px 4px; border-radius: 6px;
  font-size: 13px; font-weight: 700; color: ${({ $c }) => $c || '#475467'};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ $clickable }) => $clickable && '&:hover { background: #e6f7f9; }'}
`

const Foot = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap;
  padding-top: 8px; font-size: 12.5px; color: #667085;
`


const Empty = styled.div` padding: 14px 4px 16px; font-size: 13px; color: #667085; `
